import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  IconButton,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Download } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import InquiryPopup from './InquiryPopup';
import slider1 from '../assets/images/slider1.jpg';
import slider2 from '../assets/images/slider2.jpg';
import slider3 from '../assets/images/slider3.jpg';
import slider4 from '../assets/images/slider4.jpg';
import slider5 from '../assets/images/slider5.jpg';
import brochure from '../assets/brochure.pdf';

const SliderContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '100vh',
  minHeight: '600px',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const Slide = styled(Box)(({ theme, active }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  opacity: active ? 1 : 0,
  transition: 'opacity 1s ease-in-out',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
  },
}));

const SlideContent = styled(Box)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  textAlign: 'center',
  color: 'white',
  maxWidth: '800px',
  padding: theme.spacing(4),
}));

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const slides = [
    {
      image: slider1,
      title: 'Own a Piece of Serenity',
      subtitle: 'Premium NA plots nestled in nature’s lap at Guhagar',
    },
    {
      image: slider2,
      title: 'Nature. Investment. Future.',
      subtitle: 'Secure land ownership with long-term value and peace of mind',
    },
    
    {
      image: slider3,
      title: 'Holiday Homes That Feel Like Home',
      subtitle: 'Fully serviced homes designed for comfort and calm',
    },
    {
      image: slider4,
      title: 'Build Your Dream Getaway',
      subtitle: 'Scenic plots perfect for second homes or holiday villas',
    },
    
    {
      image: slider5,
      title: 'Cashew County — NA Plots & Holiday Homes',
      subtitle: 'Legally clear plots near Guhagar for peaceful holiday living',
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [slides.length]);

  const handlePrevious = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleNext = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const handleInquiryClick = () => {
    setInquiryOpen(true);
  };

  const handleTouchStart = (e) => {
    setTouchStartX(e.changedTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEndX(e.changedTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStartX === null || touchEndX === null) return;
    const delta = touchEndX - touchStartX;
    const threshold = 50; // px
    if (delta > threshold) {
      handlePrevious();
    } else if (delta < -threshold) {
      handleNext();
    }
    setTouchStartX(null);
    setTouchEndX(null);
  };

  return (
    <SliderContainer id="home" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
      {slides.map((slide, index) => (
        <Slide
          key={index}
          active={index === currentSlide}
          sx={{
            backgroundImage: `url(${slide.image})`,
          }}
        />
      ))}

      <SlideContent>
        <Typography
          variant={isMobile ? 'h3' : 'h1'}
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
            mb: 2,
          }}
        >
          {slides[currentSlide].title}
        </Typography>
        <Typography
          variant={isMobile ? 'h6' : 'h5'}
          component="p"
          sx={{
            mb: 4,
            textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
            opacity: 0.9,
          }}
        >
          {slides[currentSlide].subtitle}
        </Typography>
        {/* <Button
          variant="contained"
          size="large"
          startIcon={<PlayArrowIcon />}
          onClick={handleInquiryClick}
          sx={{
            px: 4,
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 600,
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
        >
          Send Inquiry 
        </Button> */}
        <Button
          component="a"
          href={brochure}
          download
          variant="contained"
          color="primary"
          startIcon={<Download />}
          sx={{ px: 4, py: 1.5, fontWeight: 700 }}
        >
          Download Brochure
        </Button>
      </SlideContent>

      <IconButton
        onClick={handlePrevious}
        sx={{
          position: 'absolute',
          left: 20,
          top: '50%',
          transform: 'translateY(-50%)',
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          color: 'white',
          zIndex: 5,
          width: 48,
          height: 48,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
          },
        }}
      >
        <ChevronLeftIcon />
      </IconButton>

      <IconButton
        onClick={handleNext}
        sx={{
          position: 'absolute',
          right: 20,
          top: '50%',
          transform: 'translateY(-50%)',
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          color: 'white',
          zIndex: 5,
          width: 48,
          height: 48,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.3)',
          },
        }}
      >
        <ChevronRightIcon />
      </IconButton>

      <Box
        sx={{
          position: 'absolute',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 1,
        }}
      >
        {slides.map((_, index) => (
          <Box
            key={index}
            onClick={() => setCurrentSlide(index)}
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: index === currentSlide ? 'white' : 'rgba(255, 255, 255, 0.5)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: 'white',
              },
            }}
          />
        ))}
      </Box>

      <InquiryPopup 
        open={inquiryOpen} 
        onClose={() => setInquiryOpen(false)} 
      />
    </SliderContainer>
  );
};

export default HeroSlider; 